import { useCallback, useEffect, useRef, useState, type CSSProperties, type PointerEvent } from "react";
import { Alert } from "@sortera/react-components";
import type { ConversationDto, MeDto, PreviewDto, ProjectDetailsDto, ProjectDto } from "../../types/index.ts";
import {
  deleteConversation,
  fetchConversations,
  fetchMe,
  fetchPreview,
  fetchProject,
  publishProject,
  fetchProjects,
} from "../../integrations/api.ts";
import type { Auth } from "../../integrations/auth.ts";
import type { PublicConfig } from "../../integrations/config.ts";
import { ApiError } from "../../integrations/http.ts";
import { Spinner } from "../../ui/spinner/index.ts";
import { ChatView, type SaveEvent } from "../chat-view/index.ts";
import { useLimitLock } from "../limit-lock/index.ts";
import { NoProjects } from "../no-projects/index.ts";
import { PreviewPanel, previewStateFrom, type PreviewEvent, type PreviewState } from "../preview-panel/index.ts";
import { ProjectGrid } from "../project-grid/index.ts";
import { ProjectHeader } from "../project-header/index.ts";
import { PublishPill } from "../publish-pill/index.ts";
import { Rail } from "../rail/index.ts";
import { Sidebar } from "../sidebar/index.ts";
import { parseRoute, routePath, type Route } from "./route.ts";
import styles from "./workspace.module.css";

const LAST_REPO = "sortera-chat:last-repo";
const PREVIEW_OPEN = "sortera-chat:preview-open";
const PREVIEW_WIDTH = "sortera-chat:preview-width";
const SIDEBAR_HIDDEN = "sortera-chat:sidebar-hidden";

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode — a convenience lost, nothing more */
  }
}

const remembered = () => read(LAST_REPO);
const remember = (repo: string) => write(LAST_REPO, repo);

/** Side by side only where both fit; on a narrow screen the preview covers the chat. */
const wide = () => window.matchMedia("(min-width: 64rem)").matches;

/** The preview's share of the main area, in percent, kept between visits. */
function rememberedWidth(): number {
  const value = Number(read(PREVIEW_WIDTH));
  return value >= 30 && value <= 75 ? value : 52;
}

export function Workspace({ auth, config }: { auth: Auth; config: PublicConfig }) {
  const { features } = config;
  const [route, setRouteState] = useState<Route>(() => parseRoute(window.location.pathname));
  const [projects, setProjects] = useState<ProjectDto[] | null>(null);
  const [project, setProject] = useState<ProjectDetailsDto | null>(null);
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [me, setMe] = useState<MeDto | null>(null);
  /** Something on this page failed. Cleared on leaving it, so it cannot follow you around. */
  const [error, setError] = useState<string | null>(null);
  /** The project list could not be fetched — then "no project yet" would be a lie. */
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [preview, setPreview] = useState<PreviewDto | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState>({ kind: "idle" });
  const [previewOpen, setPreviewOpenState] = useState(() => read(PREVIEW_OPEN) === "true" && wide());
  const [previewWidth, setPreviewWidth] = useState(rememberedWidth);
  const [sidebarOpen, setSidebarOpen] = useState(() => read(SIDEBAR_HIDDEN) !== "true");
  const toggleSidebar = (open: boolean) => {
    write(SIDEBAR_HIDDEN, String(!open));
    setSidebarOpen(open);
  };
  const split = useRef<HTMLDivElement>(null);

  const setPreviewOpen = useCallback((open: boolean) => {
    setPreviewOpenState(open);
    // Only a choice made where both fit is remembered — closing the overlay on a phone is not
    // a wish to hide it on the desktop.
    if (wide()) write(PREVIEW_OPEN, String(open));
  }, []);

  const navigate = useCallback((next: Route, replace = false) => {
    const path = routePath(next);
    if (path !== window.location.pathname) window.history[replace ? "replaceState" : "pushState"](null, "", path);
    setRouteState(next);
    setMenuOpen(false);
    setError(null);
  }, []);

  useEffect(() => {
    const onPop = () => setRouteState(parseRoute(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /** A send was refused at the limit while `me` said there was room; the next answer from the server settles it. */
  const [limitRefused, setLimitRefused] = useState(false);
  const [publishing, setPublishing] = useState(false);
  /** What a publish or a save that did not happen has to say; gone after a while. */
  const [notice, setNotice] = useState<{ variant: "info" | "warning"; text: string } | null>(null);
  const refreshMe = useCallback(
    () =>
      void fetchMe()
        .then((fresh) => (setMe(fresh), setLimitRefused(false)))
        .catch(() => undefined),
    [],
  );
  const onLimitReached = useCallback(() => {
    setLimitRefused(true);
    refreshMe();
  }, [refreshMe]);
  const lock = useLimitLock(me, limitRefused, refreshMe);
  const refreshConversations = useCallback(() => {
    if (route.repo) void fetchConversations(route.repo).then(setConversations).catch(() => undefined);
  }, [route.repo, features.preview]);

  /** At start, and again when someone without a project has made one in the Factory. */
  const loadProjects = useCallback(async () => {
    try {
      const list = await fetchProjects();
      setProjects(list);
      setProjectsError(null);
      // No project in the address: open the last one used, or the only one there is.
      if (!parseRoute(window.location.pathname).repo) {
        const last = remembered();
        const pick = list.find((p) => p.repo === last) ?? (list.length === 1 ? list[0] : undefined);
        if (pick) navigate({ repo: pick.repo, conversationId: null }, true);
      }
    } catch (e) {
      setProjects([]);
      setProjectsError(e instanceof ApiError ? e.message : "Projekten kunde inte hämtas.");
    }
  }, [navigate]);

  useEffect(() => {
    refreshMe();
    void loadProjects();
  }, [loadProjects, refreshMe]);

  // A new chat starts without the preview: what is in it was made in another chat. It opens
  // again when this one builds something. Not remembered — the person's own choice stands.
  const newChat = Boolean(route.repo) && !route.conversationId;
  useEffect(() => {
    if (newChat) setPreviewOpenState(false);
  }, [newChat, route.repo]);

  useEffect(() => {
    setProject(null);
    setConversations([]);
    setPreview(null);
    setPreviewState({ kind: "idle" });
    if (!route.repo) return;
    remember(route.repo);
    let cancelled = false;
    if (features.preview) {
      void fetchPreview(route.repo)
        .then((p) => !cancelled && setPreview(p))
        .catch(() => undefined);
    }
    fetchProject(route.repo)
      .then((p) => !cancelled && setProject(p))
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : "Projektet kunde inte hämtas."));
    void fetchConversations(route.repo).then((list) => !cancelled && setConversations(list)).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [route.repo]);

  const onTurnFinished = useCallback(() => {
    refreshConversations();
    refreshMe();
    // A save changes what there is to publish; the header shows it a moment after the turn.
    if (route.repo) {
      const repo = route.repo;
      setTimeout(() => void fetchProject(repo).then(setProject).catch(() => undefined), 4000);
    }
  }, [refreshConversations, refreshMe, route.repo]);

  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 10_000);
    return () => clearTimeout(timer);
  }, [notice]);

  // While a deploy runs, the header follows it until the address is there or the run has failed.
  const deploying = project?.testEnvironment.state === "deploying";
  useEffect(() => {
    if (!deploying || !route.repo) return;
    const repo = route.repo;
    const timer = setTimeout(() => void fetchProject(repo).then(setProject).catch(() => undefined), 15_000);
    return () => clearTimeout(timer);
  }, [deploying, project, route.repo]);

  const onPublish = useCallback(async () => {
    if (!route.repo) return;
    const repo = route.repo;
    setPublishing(true);
    try {
      const result = await publishProject(repo);
      setNotice({ variant: result.outcome === "published" || result.outcome === "nothing" ? "info" : "warning", text: result.message });
      setProject(await fetchProject(repo));
    } catch (e) {
      setNotice({ variant: "warning", text: e instanceof ApiError ? e.message : "Det gick inte att lägga upp just nu. Försök igen om en stund." });
    } finally {
      setPublishing(false);
    }
  }, [route.repo]);

  const onSaveEvent = useCallback((event: SaveEvent) => {
    if (!event.saved) setNotice({ variant: "warning", text: event.message });
  }, []);

  const onPreviewEvent = useCallback(
    (event: PreviewEvent) => {
      setPreviewState(previewStateFrom(event));
      // Every build opens the panel, so what was just made is in view while it is built.
      if (event.status === "building" && wide()) setPreviewOpen(true);
      // A finished build, or a snapshot while the agent works: either way there is a newer version.
      if (!route.repo || !(event.status === "ready" || event.version)) return;
      void fetchPreview(route.repo).then(setPreview).catch(() => undefined);
    },
    [route.repo, setPreviewOpen],
  );

  /** Dragging the hairline between chat and preview. */
  const startResize = (down: PointerEvent<HTMLDivElement>) => {
    const box = split.current?.getBoundingClientRect();
    if (!box) return;
    down.currentTarget.setPointerCapture(down.pointerId);
    const move = (e: globalThis.PointerEvent) => {
      const percent = ((box.right - e.clientX) / box.width) * 100;
      setPreviewWidth(Math.min(75, Math.max(30, percent)));
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      document.body.style.removeProperty("cursor");
      setPreviewWidth((w) => (write(PREVIEW_WIDTH, String(Math.round(w))), w));
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  /** A project made here is done: fetch the list it is now in, and open it. */
  const onCreated = useCallback(
    async (repo: string) => {
      await loadProjects();
      navigate({ repo, conversationId: null });
    },
    [loadProjects, navigate],
  );

  const onDelete = async (id: string) => {
    try {
      await deleteConversation(id);
      setConversations((list) => list.filter((c) => c.id !== id));
      if (route.conversationId === id) navigate({ repo: route.repo, conversationId: null });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Konversationen kunde inte tas bort.");
    }
  };

  const current = conversations.find((c) => c.id === route.conversationId) ?? null;

  return (
    <div className={styles.layout} data-menu-open={menuOpen} data-sidebar={sidebarOpen ? "open" : "hidden"}>
      <div className={styles.nav}>
        <Rail
          view={route.repo ? "chat" : "projects"}
          userName={me?.name ?? auth.account?.name ?? ""}
          onChat={() => {
            // The last project used in this browser may be someone else's; then there is
            // nothing to chat in yet, and the start page says how to get something.
            const last = remembered();
            const repo = route.repo ?? (projects?.some((p) => p.repo === last) ? last : null);
            navigate({ repo, conversationId: repo ? route.conversationId : null });
          }}
          onProjects={() => navigate({ repo: null, conversationId: null })}
          onHome={() => navigate({ repo: null, conversationId: null })}
          onSignOut={() => void auth.signOut()}
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => toggleSidebar(!sidebarOpen)}
        />
        <Sidebar
          projects={projects}
          repo={route.repo}
          conversations={conversations}
          conversationId={route.conversationId}
          onSelectProject={(repo) => navigate({ repo, conversationId: null })}
          onNewConversation={() => navigate({ repo: route.repo, conversationId: null })}
          onSelectConversation={(id) => navigate({ repo: route.repo, conversationId: id })}
          onDeleteConversation={(id) => void onDelete(id)}
          onHide={() => toggleSidebar(false)}
        />
      </div>
      <button type="button" className={styles.scrim} aria-label="Stäng menyn" onClick={() => setMenuOpen(false)} />

      <main className={styles.main}>
        <ProjectHeader
          project={project}
          title={current?.title ?? null}
          onOpenMenu={() => setMenuOpen(true)}
          onNewConversation={() => navigate({ repo: route.repo, conversationId: null })}
          previewOpen={previewOpen}
          previewState={previewState}
          onTogglePreview={features.preview ? () => setPreviewOpen(!previewOpen) : undefined}
        />
        {notice && (
          <div className={styles.banner} aria-live="polite">
            <Alert variant={notice.variant}>{notice.text}</Alert>
          </div>
        )}
        {(error ?? projectsError) && (
          <div className={styles.banner}>
            <Alert variant="critical" title="Något gick fel">
              {error ?? projectsError}
            </Alert>
          </div>
        )}
        {project ? (
          <div
            className={styles.split}
            ref={split}
            data-preview={features.preview && previewOpen}
            style={{ "--sc-preview-width": `${previewWidth}%` } as CSSProperties}
          >
            <ChatView
              key={project.repo}
              project={project}
              me={me}
              models={config.models}
              lock={lock}
              onLimitReached={onLimitReached}
              conversationId={route.conversationId}
              onOpenConversation={(id) => {
                navigate({ repo: project.repo, conversationId: id });
                refreshConversations();
              }}
              onTurnFinished={onTurnFinished}
              onPreviewEvent={onPreviewEvent}
              onSaveEvent={onSaveEvent}
              action={<PublishPill unpublished={project.unpublished} publishing={publishing} onPublish={() => void onPublish()} />}
            />
            {features.preview && previewOpen && (
              <>
                <div
                  className={styles.divider}
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Dra för att ändra bredd"
                  onPointerDown={startResize}
                />
                <div className={styles.preview}>
                  <PreviewPanel preview={preview} state={previewState} onClose={() => setPreviewOpen(false)} />
                </div>
              </>
            )}
          </div>
        ) : route.repo ? (
          !error && (
            <p className={styles.placeholder}>
              <Spinner /> Hämtar projektet …
            </p>
          )
        ) : projects?.length ? (
          <ProjectGrid
            projects={projects}
            defaultEmail={me?.email ?? ""}
            onPick={(repo) => navigate({ repo, conversationId: null })}
            onCreated={features.createProject ? (repo) => void onCreated(repo) : undefined}
          />
        ) : projects ? (
          // A failed fetch also leaves the list empty; then the banner says so, not this page.
          !projectsError && (
            <NoProjects
              defaultEmail={me?.email ?? ""}
              onCreated={features.createProject ? (repo) => void onCreated(repo) : undefined}
              onRefresh={loadProjects}
            />
          )
        ) : (
          <p className={styles.placeholder}>
            <Spinner /> Hämtar dina projekt …
          </p>
        )}
      </main>
    </div>
  );
}
